'use client';

import Inventory2Icon from '@mui/icons-material/Inventory2';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { updateInventoryAuditorItem, type InventoryAuditorItemRow } from '@/hooks/useInventoryAuditorInventory';
import { useNotification } from '@/contexts/NotificationContext';
import {
  INVENTORY_AUDITOR_SIZES,
  INVENTORY_AUDITOR_UOM_PACK_TYPES,
} from '@/stack-shared/constants/inventory-auditor-catalog.constants';

function displayUpc(raw: string | null | undefined): string {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return '—';
  if (digits.length === 13 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

function withCurrentOption(options: readonly string[], current: string): string[] {
  const t = current.trim();
  if (t && !options.includes(t)) return [t, ...options];
  return [...options];
}

type Props = {
  open: boolean;
  item: InventoryAuditorItemRow | null;
  onClose: () => void;
  onSaved: (item: InventoryAuditorItemRow) => void;
};

const InventoryAuditorCatalogItemEditDialog: React.FC<Props> = ({ open, item, onClose, onSaved }) => {
  const { showError, showSuccess } = useNotification();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scanContext, setScanContext] = useState('');
  const [uom, setUom] = useState('');
  const [size, setSize] = useState('');
  const [deleted, setDeleted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setName(item.name ?? '');
    setDescription(item.description ?? '');
    setScanContext(item.scanContext ?? '');
    setUom(item.uomPackType ?? '');
    setSize(item.size ?? '');
    setDeleted(item.deleted === true);
    setSaving(false);
  }, [open, item]);

  const uomOptions = useMemo(() => withCurrentOption(INVENTORY_AUDITOR_UOM_PACK_TYPES, uom), [uom]);
  const sizeOptions = useMemo(() => withCurrentOption(INVENTORY_AUDITOR_SIZES, size), [size]);

  const save = async () => {
    if (!item) return;
    const trimmedName = name.trim();
    if (!trimmedName) {
      showError('Name is required');
      return;
    }
    if (!uom.trim()) {
      showError('UOM is required');
      return;
    }
    if (!size.trim()) {
      showError('Size is required');
      return;
    }
    setSaving(true);
    try {
      const updated = await updateInventoryAuditorItem({
        id: item.id,
        name: trimmedName,
        description: description.trim() === '' ? null : description.trim(),
        scanContext: scanContext.trim() === '' ? null : scanContext.trim(),
        uomPackType: uom.trim(),
        size: size.trim(),
        deleted,
      });
      showSuccess('Catalog item updated');
      onSaved(updated);
      onClose();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit catalog item</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2, pt: 1, mb: 2, alignItems: 'flex-start' }}>
          <Avatar variant="rounded" src={item?.imageUrl || undefined} sx={{ width: 72, height: 72, flexShrink: 0 }}>
            <Inventory2Icon />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" color="text.secondary">
              UPC {displayUpc(item?.upc)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              UPC cannot be changed here.
            </Typography>
          </Box>
        </Box>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          fullWidth
          required
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Autocomplete
            size="small"
            options={uomOptions}
            value={uom || null}
            onChange={(_, v) => setUom(v ?? '')}
            renderInput={(params) => <TextField {...params} label="UOM" required />}
            sx={{ minWidth: 160, flex: 1 }}
          />
          <Autocomplete
            size="small"
            options={sizeOptions}
            value={size || null}
            onChange={(_, v) => setSize(v ?? '')}
            renderInput={(params) => <TextField {...params} label="Size" required />}
            sx={{ minWidth: 160, flex: 1 }}
          />
        </Box>
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Context"
          value={scanContext}
          onChange={(e) => setScanContext(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
          helperText="Notes from scan confirm or admin."
        />
        <FormControlLabel
          control={<Checkbox checked={deleted} onChange={(_, v) => setDeleted(v)} size="small" />}
          label="Deleted"
          sx={{ mt: 1, display: 'block' }}
        />
        <Typography variant="caption" color="text.secondary">
          Deleted items stay in the catalog but are hidden unless Show deleted is on.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void save()} disabled={saving} sx={{ textTransform: 'none' }}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryAuditorCatalogItemEditDialog;
